<template>
  <v-hover v-slot="{ hover }">
    <div>
      <template>
        <v-card class="pa-1 mb-1 application-container" :elevation="hover ? 2 : 2" :class="{ 'on-hover': hover }">
          <div class="title-container">
            <div class="description">
              <v-card-title class="title">
                {{ application.name }}
              </v-card-title>
              <v-card-subtitle v-for="update in productionUpdates" :key="update.applicationId" class="subtitle">
                <button type="button" class="update-link" @click="openChanges(update)"
                  :aria-label="`View commits for ${application.name} from ${update.deployment.version} to ${firstReleaseVersion}`">
                  <span v-if="update.configuration.instanceIdentifier">{{ update.configuration.instanceIdentifier.displayName }}: </span>
                  {{ update.deployment.version.replace('-RELEASE', '') }} --> {{ firstReleaseVersion }}
                </button>
              </v-card-subtitle>
            </div>
            <v-btn icon small class="ignore-btn" :aria-label="ignored ? 'Unignore' : 'Ignore'"
              :title="ignored ? 'Unignore' : 'Ignore'" @click.stop="$emit(ignored ? 'unignore' : 'ignore')">
              <v-icon small>{{ ignored ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
            </v-btn>
          </div>

        </v-card>
        <CommitChangesDialog v-model="showChanges" :title="changeTitle" :changes="changes" :loading="loadingChanges"
          :error="changesError" @retry="loadChanges" />
      </template>
    </div>
  </v-hover>
</template>

<script>
import ApplicationService from '@/services/ApplicationService';
import CommitChangesDialog from './CommitChangesDialog.vue';

export default {
  name: 'ApplicationWithUpdate',
  components: { CommitChangesDialog },
  props: [
    'application',
    "deploymentCache",
    "ignored"
  ],
  data() {
    return {
      showChanges: false,
      changes: [],
      loadingChanges: false,
      changesError: '',
      selectedUpdate: null,
      requestId: 0,
    };
  },
  computed: {
    firstReleaseVersion() {
      return this.application.releaseVersions[0];
    },
    productionUpdates() {
      return this.application.environmentConfigurations
        .filter(configuration => configuration.environmentType.name === 'PRODUCTION_ENVIRONMENT')
        .map(configuration => {
          const applicationId = configuration.instanceIdentifier && configuration.instanceIdentifier.name
            ? `${this.application.name}-${configuration.instanceIdentifier.name}`
            : this.application.name;
          const deployment = this.deploymentCache && this.deploymentCache[applicationId]
            && this.deploymentCache[applicationId].PRODUCTION_ENVIRONMENT;
          return { applicationId, configuration, deployment };
        })
        .filter(update => update.deployment && update.deployment.version && this.firstReleaseVersion);
    },
    changeTitle() {
      if (!this.selectedUpdate) return '';
      const update = this.selectedUpdate;
      return `${update.applicationId}: ${update.fromVersion.replace('-RELEASE', '')} → ${update.toVersion}`;
    }
  },
  methods: {
    openChanges(update) {
      this.selectedUpdate = {
        applicationName: this.application.name,
        applicationId: update.applicationId,
        fromVersion: update.deployment.version,
        toVersion: this.firstReleaseVersion
      };
      this.showChanges = true;
      this.loadChanges();
    },
    async loadChanges() {
      if (!this.selectedUpdate) return;
      const update = this.selectedUpdate;
      const requestId = ++this.requestId;
      this.changes = [];
      this.changesError = '';
      this.loadingChanges = true;
      try {
        const changes = await ApplicationService.getAvailableUpdateChanges(update.applicationName, update.fromVersion, update.toVersion);
        if (!Array.isArray(changes)) throw new Error('Invalid commits response');
        if (requestId === this.requestId) this.changes = changes;
      } catch (error) {
        if (requestId === this.requestId) this.changesError = 'Could not load commits. Please try again.';
      } finally {
        if (requestId === this.requestId) this.loadingChanges = false;
      }
    }
  },
  beforeDestroy() {
    this.requestId++;
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.application-container {
  position: relative;
  text-align: left;
}

.application-container .title-container {
  display: flex;
  align-items: center;
}

.application-container .title-container .title {
  padding-bottom: 0;
}

.application-container .title-container .description {
  flex-grow: 1;
}

.update-link {
  width: 100%;
  text-align: left;
  color: inherit;
}

.update-link:hover,
.update-link:focus {
  text-decoration: underline;
}

.application-container .edit-btn {
  padding: 32px;
}

.ignore-btn {
  position: absolute;
  top: 6px;
  right: 6px;
}

.application-container .title {
  color: #4e4b47;
  font-weight: bold;
  text-align: left;
}

.application-container .subtitle {
  text-align: left;
  padding-top: 0;
  margin-top: 0;
}

.expandable-title {
  min-height: 64px;
}

.v-expansion-panel-header--active,
.expandable-title:hover {
  color: #42b983;
}

.v-expansion-panel-header .subtitle {
  line-height: 24px;
  font-size: 0.9em;
  color: rgba(0, 0, 0, 0.6);
}

.v-expansion-panel-header--active:before {
  color: inherit;
}

.expandable-content .expandable-content-container {
  padding: 0 0 0 18px;
}


.dialog-container .title-container .title {
  padding: 36px 48px 0;
}

.dialog-container h2 {
  text-align: left;
}

.dialog-container .configuration-container {
  text-align: left;
}

.dialog-container .environment-configuration-container {
  margin: 0 24px 24px;
}

.dialog-container .environment-configuration-container .v-subheader {
  height: auto;
  margin-top: 12px;
}

.dialog-container .environment-configuration-container h3 {
  color: #42b983;
  font-size: x-large;
}

.dialog-container .environment-configuration-container .select-input {
  padding: 0;
}


.dialog-container .environment-configuration-container .header {
  display: flex;
  align-items: center;
}


.dialog-container .environment-configuration-container .header h3 {
  flex-grow: 1;
}

.dialog-container .v-card__actions {
  padding: 24px;
}

.environment-configuration-divider {
  display: none;
}

.environment-configuration-group-divider+.environment-configuration-information-container>div:first-child .environment-configuration-divider {
  display: block;
  width: 100%;
  height: 1px;
  margin: 15px 0;
  background-color: #dcdee0;
}</style>
